import { memo, ReactNode, useCallback } from 'react';
import { Doc, DocStatus, UserType } from '@app/models';
import { RenderFilterProps, SearchTemplate } from '@app/templates';
import { getPartners, PageableParams } from '@app/api';
import CompaniesFilter, {
  CompaniesFilterData,
} from './components/CompaniesFilter';
import { Info } from '@app/components';
import { useNavigate } from 'react-router-dom';
import {
  fullName,
  getCompanyTypeShortName,
  getPartnerEntity,
  getPartnerIndividual,
} from '@app/helpers';
import { useUserContext } from '@app/providers';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';

interface Props {
  title: string;
  controls: ReactNode[];
}

function PartnersSearch(props: Props) {
  const { title, controls } = props;
  const { user, company } = useUserContext();
  const navigate = useNavigate();

  const onClickCompany = useCallback(
    (item: Doc) => () => {
      const partnerCompany = getPartnerEntity(item, company);
      if (!!partnerCompany) {
        navigate(`/profile/partners/${UserType.ENTITY}/${partnerCompany.id}`);
        return;
      }
      return navigate(
        `/profile/partners/${UserType.INDIVIDUAL}/${
          getPartnerIndividual(item, user.id)!.id
        }`
      );
    },
    [company, navigate, user.id]
  );

  const renderItem = useCallback(
    (item: Doc) => {
      const partnerCompany = getPartnerEntity(item, company);
      const partnerUser = getPartnerIndividual(item, user.id);
      return (
        <Card
          onClick={onClickCompany(item)}
          className="w-full cursor-pointer hover:bg-accent transition border-none shadow-lg mb-4"
        >
          <CardContent className="grid grid-cols-1 sm:grid-cols-1 gap-4 items-center p-4">
            {!!partnerCompany && (
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground mb-1">
                  БИН партнера
                </span>
                <span className="font-medium break-all truncate">
                  {partnerCompany.bin}
                </span>
              </div>
            )}
            {!!partnerCompany && (
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground mb-1">
                  Наименование партнера
                </span>
                <span className="font-medium truncate">{`${getCompanyTypeShortName(
                  partnerCompany.type
                )} ${partnerCompany.name}`}</span>
              </div>
            )}
            {!!partnerUser && (
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground mb-1">
                  ИИН партнера
                </span>
                <span className="font-medium break-all truncate">
                  {partnerUser.iin}
                </span>
              </div>
            )}
            {!!partnerUser && (
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-muted-foreground mb-1">
                  Партнер
                </span>
                <span className="font-medium truncate">
                  {fullName(partnerUser)}
                </span>
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-muted-foreground mb-1">
                Количество договоров
              </span>
              <Badge variant="secondary" className="w-fit">
                {`${
                  item.docs.filter((doc) => doc.status !== DocStatus.FINISHED)
                    .length
                }/${item.docs.length}`}
              </Badge>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-muted-foreground mb-1">Регион</span>
              <span className="font-medium truncate">
                {item.recipientCompanyId
                  ? item.recipientCompany?.kato
                    ? item.recipientCompany.kato.nameRu
                    : 'Не указан'
                  : item.recipient?.kato
                  ? item.recipient.kato.nameRu
                  : 'Не указан'}
              </span>
            </div>
          </CardContent>
        </Card>
      );
    },
    [company, onClickCompany, user.id]
  );

  const getData = useCallback(
    (params: PageableParams, filter?: CompaniesFilterData) => {
      const defaultParams: Partial<Doc> = filter!;
      if (user.type === UserType.INDIVIDUAL) {
        defaultParams.ownerId = user.id;
      } else {
        defaultParams.companyId = company!.id;
      }
      return getPartners({
        ...params,
        ...defaultParams,
      });
    },
    [company, user]
  );

  const renderFilter = useCallback(
    (filterProps: RenderFilterProps<CompaniesFilterData>) => {
      return <CompaniesFilter {...filterProps} />;
    },
    []
  );

  return (
    <>
      <SearchTemplate<Doc, CompaniesFilterData>
        columns={1}
        renderItem={renderItem}
        getData={getData}
        renderFilter={renderFilter}
        title={title}
        controls={controls}
        variant="paper"
        viewControl={false}
        emptyTitle={
          <span>
            Тут появится список партнеров, с кем Вы заключили договор на{' '}
            <a href="https://smrkz.kz/" target="_blank" rel="noreferrer">
              https://smrkz.kz/
            </a>
          </span>
        }
      />
    </>
  );
}

export default memo(PartnersSearch);
